import { AllEvent, AttendeeResponse, InternalInvitationResponseAction } from "../interfaces/identity/calendar/Events";

export type CalendarInviteStatus = AttendeeResponse["inviteStatus"];

export const getResponseActionForStatus = (
  status?: CalendarInviteStatus | "ORGANIZER" | null,
): InternalInvitationResponseAction | null => {
  switch (status) {
    case "ACCEPTED": return "ACCEPT";
    case "TENTATIVE": return "TENTATIVE";
    case "DECLINED": return "DECLINE";
    default: return null;
  }
};

export const canChangeInvitationResponse = (
  status?: CalendarInviteStatus | "ORGANIZER" | null,
): boolean => status === "INVITED"
  || status === "ACCEPTED"
  || status === "TENTATIVE"
  || status === "DECLINED";

export interface RsvpPresentation {
  label: string;
  shortLabel: string;
  textClass: string;
  badgeClass: string;
  isAwaitingResponse: boolean;
}

export const getAttendeeInviteStatus = (
  event: Pick<AllEvent, "ownerUserID" | "attendees">,
  userId?: string | null,
): CalendarInviteStatus | "ORGANIZER" | null => {
  if (!userId) return null;
  if (event.ownerUserID === userId) return "ORGANIZER";

  return event.attendees?.find((attendee) => attendee.ref === userId)?.inviteStatus ?? null;
};

export const getRsvpPresentation = (
  status?: CalendarInviteStatus | "ORGANIZER" | null,
): RsvpPresentation => {
  switch (status) {
    case "ORGANIZER":
      return {
        label: "Organizer",
        shortLabel: "Organizer",
        textClass: "text-[#7E57C2]",
        badgeClass: "border-purple-200 bg-purple-50 text-[#6D46B2]",
        isAwaitingResponse: false,
      };
    case "ACCEPTED":
      return {
        label: "Accepted",
        shortLabel: "Accepted",
        textClass: "text-emerald-600",
        badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
        isAwaitingResponse: false,
      };
    case "DECLINED":
      return {
        label: "Declined",
        shortLabel: "Declined",
        textClass: "text-red-600",
        badgeClass: "border-red-200 bg-red-50 text-red-700",
        isAwaitingResponse: false,
      };
    case "TENTATIVE":
      return {
        label: "Tentative",
        shortLabel: "Tentative",
        textClass: "text-amber-600",
        badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
        isAwaitingResponse: true,
      };
    case "CANCELLED":
      return {
        label: "Cancelled",
        shortLabel: "Cancelled",
        textClass: "text-gray-500",
        badgeClass: "border-gray-200 bg-gray-50 text-gray-600",
        isAwaitingResponse: false,
      };
    case "INVITED":
      return {
        label: "Awaiting response",
        shortLabel: "Awaiting RSVP",
        textClass: "text-amber-600",
        badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
        isAwaitingResponse: true,
      };
    default:
      return {
        label: "No response required",
        shortLabel: "Meeting",
        textClass: "text-gray-500",
        badgeClass: "border-gray-200 bg-gray-50 text-gray-600",
        isAwaitingResponse: false,
      };
  }
};
